import {Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {IBerater} from '../shared/model/berater.model';
import {environment} from '../../environments/environment';
import {Personaldaten} from '../shared/model/personaldaten.model';
import {IWerdegang} from '../shared/model/werdegang.model';
import {IAusbildung} from '../shared/model/ausbildung.model';
import {IProjekt} from '../shared/model/projekt.model';
import {IZertifikat} from '../shared/model/zertifikat.model';
import {IFaehigkeit} from '../shared/model/faehigkeit.model';
import {CatalogAssignment} from '../shared/model/catalog-assignment.model';
import {ISchulung} from '../shared/model/schulung.model';
import {Note} from '../shared/model/notes.model';
import {Aufgabe} from '../shared/model/aufgabe.model';

type EntityResponseType = HttpResponse<IBerater>;

export class TaskMissingError extends Error {}

export interface ProjectSaveResult {
  project: IProjekt;
  faehigkeiten: IFaehigkeit[];
}

@Injectable({providedIn: 'root'})
export class BeraterService {
  public resourceUrl = environment.apiConfig.uri + 'api/berater';

  constructor(protected http: HttpClient) {
  }

  find(id: string): Observable<EntityResponseType> {
    return this.http.get<IBerater>(`${this.resourceUrl}/${id}`, {
      observe: 'response'
    });
  }

  updateSummary(summary: string, id: string): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/summary`,
      summary
    );
  }

  updatePersonaldaten(
    personaldaten: Personaldaten,
    id: string
  ): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/personaldaten`,
      personaldaten
    );
  }

  updateWerdegang(werdegang: IWerdegang[], id: string): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/werdegang`,
      werdegang
    );
  }

  updateAusbildung(
    ausbildung: IAusbildung[],
    id: string
  ): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/ausbildung`,
      ausbildung
    );
  }

  listProjects(consultantId: string): Observable<IProjekt[]> {
    return this.http.get<IProjekt[]>(`${this.resourceUrl}/${consultantId}/projects`);
  }

  /** Speichern & freigeben eines neuen Projekts: volle Prüfung im Backend. */
  createProject(projekt: IProjekt, consultantId: string): Observable<ProjectSaveResult> {
    return this.createAt(`${this.resourceUrl}/${consultantId}/projects`, projekt, consultantId);
  }

  /** Speichern & freigeben; bei einem nie freigegebenen Entwurf ist das die Erstfreigabe. */
  updateProject(projekt: IProjekt, consultantId: string, projectId: string): Observable<ProjectSaveResult> {
    return this.http.put<void>(
      `${this.resourceUrl}/${consultantId}/projects/${projectId}`,
      projekt
    ).pipe(switchMap(() => this.reloadProject(consultantId, projectId)));
  }

  /** NP20-1057: Zwischenstand eines neuen Projekts — nur Längen- und Wertegrenzen, keine Pflichtfelder. */
  createProjectDraft(projekt: IProjekt, consultantId: string): Observable<ProjectSaveResult> {
    return this.createAt(`${this.resourceUrl}/${consultantId}/projects/drafts`, projekt, consultantId);
  }

  /** NP20-1057: Zwischenstand über einem bestehenden Projekt; der freigegebene Stand bleibt unberührt. */
  saveProjectDraft(projekt: IProjekt, consultantId: string, projectId: string): Observable<ProjectSaveResult> {
    return this.http.put<unknown>(
      `${this.resourceUrl}/${consultantId}/projects/${projectId}/draft`,
      projekt
    ).pipe(switchMap(() => this.reloadProject(consultantId, projectId)));
  }

  saveProjectTask(task: Aufgabe, consultantId: string, projectId: string, draft: boolean): Observable<ProjectSaveResult> {
    return this.find(consultantId).pipe(
      map(response => this.withTask(response.body?.projekte?.find(p => p.uuid === projectId), task)),
      switchMap(project => draft
        ? this.saveProjectDraft(project, consultantId, projectId)
        : this.updateProject(project, consultantId, projectId))
    );
  }

  private withTask(project: IProjekt | undefined, task: Aufgabe): IProjekt {
    if (!project) throw new TaskMissingError();
    const tasks = project.tasks ?? [];
    if (!task.uuid) return {...project, tasks: [...tasks, task]};
    const idx = tasks.findIndex(t => t.uuid === task.uuid);
    if (idx < 0) throw new TaskMissingError();
    return {...project, tasks: tasks.map((t, i) => i === idx ? task : t)};
  }

  /** NP20-1057: verwirft nur den Entwurf über einer Freigabe; danach gilt wieder der freigegebene Stand. */
  discardProjectDraft(consultantId: string, projectId: string): Observable<ProjectSaveResult> {
    return this.http.delete<void>(`${this.resourceUrl}/${consultantId}/projects/${projectId}/draft`)
      .pipe(switchMap(() => this.reloadProject(consultantId, projectId)));
  }

  private createAt(url: string, projekt: IProjekt, consultantId: string): Observable<ProjectSaveResult> {
    return this.http.post<IProjekt | null>(url, projekt, {observe: 'response'}).pipe(
      switchMap(response => {
        // Cross-origin (ng serve gegen :8080) liest der Browser `Location` nicht aus; der Entwurf-Endpunkt
        // liefert die uuid zusätzlich im Body.
        const fromLocation = response.headers.get('Location')?.split('/').pop();
        return this.reloadProject(consultantId, fromLocation || response.body?.uuid || '');
      })
    );
  }

  /** Nachladen statt der Einzelantwort: Fähigkeiten und Freigabestatus kommen so aus demselben Stand. */
  private reloadProject(consultantId: string, projectId: string): Observable<ProjectSaveResult> {
    return this.find(consultantId).pipe(
      map(response => {
        const body = response.body;
        const project = body?.projekte?.find(p => p.uuid === projectId);
        if (!project) throw new Error(`Saved project with uuid '${projectId}' not found in reload response`);
        return {project, faehigkeiten: body?.faehigkeiten ?? []};
      })
    );
  }

  /**
   * NP20-760: Nach dem Loeschen den Berater nachladen und die neu berechneten Faehigkeiten
   * zurueckgeben — genau wie createProject und updateProject. Ohne das blieben die Faehigkeiten
   * des geloeschten Projekts bis zum naechsten Seitenaufruf in der Liste stehen.
   */
  deleteProjectById(projectId: string, consultantId: string): Observable<IFaehigkeit[]> {
    return this.http.delete<void>(
      `${this.resourceUrl}/${consultantId}/projects/${projectId}`
    ).pipe(
      switchMap(() => this.find(consultantId)),
      map(response => response.body?.faehigkeiten ?? [])
    );
  }

  updateZertifikate(
    zertifikat: IZertifikat[],
    id: string
  ): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/zertifikate`,
      zertifikat
    );
  }

  updateSchulungen(schulungen: ISchulung[], id: string): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/schulungen`,
      schulungen
    );
  }

  updateFaehigkeiten(faehigkeiten: IFaehigkeit[], id: string): Observable<IBerater> {
    return this.http.put<void>(`${this.resourceUrl}/${id}/faehigkeiten`, faehigkeiten).pipe(
      switchMap(() => this.find(id)),
      map(response => response.body!)
    );
  }

  /**
   * NP20-889: Batch-Upsert der Katalog-Zuweisungen. Nicht mitgeschickte Kataloge bleiben
   * unangetastet; innerhalb eines mitgeschickten Katalogs ersetzt ratings dessen kompletten
   * Bewertungssatz. Die Antwort ist der vollstaendige Stand nach dem Schreiben.
   * Zum Entfernen einer Zuweisung dient deleteCatalogAssignment.
   */
  upsertCatalogAssignments(consultantId: string, assignments: CatalogAssignment[]): Observable<CatalogAssignment[]> {
    return this.http.patch<CatalogAssignment[]>(
      `${this.resourceUrl}/${consultantId}/catalog-assignments`,
      assignments
    );
  }

  /** NP20-889: Idempotent — auch eine nicht (mehr) vorhandene Zuweisung liefert 204. */
  deleteCatalogAssignment(consultantId: string, catalogId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.resourceUrl}/${consultantId}/catalog-assignments/${catalogId}`
    );
  }

  updateVersteckt(versteckt: boolean, id: string): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/versteckt`,
      versteckt
    );
  }

  updateApplicant(consultant: IBerater) {
    return this.http.patch(`${environment.apiConfig.uri}api/office/berater/${consultant.id}`, consultant)
  }

  updateFreelancer(consultant: IBerater) {
    return this.http.patch(`${environment.apiConfig.uri}api/office/berater/${consultant.id}`, consultant)
  }

  updateNotes(id: string, note: Note): Observable<IBerater> {
    return this.http.put<IBerater>(
      `${this.resourceUrl}/${id}/notes`,
      {note}
    );
  }
}
